import { useMemo, useState, type ReactNode } from 'react';
import { useIsMobile } from '../../hooks/useViewport';
import styles from './ResponsiveTable.module.css';

export interface Column<T> {
  /** Unique key (React `key` + column identifier). */
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /**
   * Controls how the column collapses on mobile:
   *  - `primary`   → card title (one per card; first by default)
   *  - `secondary` → small subtitle under the title
   *  - `body`      → key/value row in the card body
   *  - `hidden`    → omitted on mobile entirely
   *  - unset       → treated as `body` unless it's the first column
   */
  mobile?: 'primary' | 'secondary' | 'body' | 'hidden';
  align?: 'left' | 'right' | 'center';
  /** Optional width hint for the desktop table (`col.width`). */
  width?: string;
  /**
   * When true, the column header is clickable to sort. The
   * comparator is `sortKey` (a property accessor) or the column's
   * `cell` text otherwise. Active sort column shows an ↑ / ↓ arrow.
   * Phase 3 follow-up — backward compatible with existing tables
   * that don't set the flag.
   */
  sortable?: boolean;
  /**
   * Sort-value extractor for this column. Returns the comparable
   * value (number / string / Date) for a row. Defaults to
   * `cell(row)` when the cell render produces a primitive string,
   * otherwise falls back to `String(cell(row))` which is fine for
   * text columns but loses numeric ordering — define `sortKey`
   * explicitly for numeric / date columns.
   */
  sortKey?: (row: T) => string | number | Date;
}

export interface ResponsiveTableProps<T> {
  /** Optional server pagination notification; local sorting stays available. */
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  mobileSort?: boolean;
  /** Rows already sorted across all pages by the server. */
  serverSorted?: boolean;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /**
   * A row's own buttons on mobile (29 Oct 2026). With `onRowClick` each
   * mobile card is a `<button>`, which cannot hold another button, so these
   * render in a strip under the card, outside it. Desktop never calls this:
   * the cells carry the same buttons there (a page hides them from its
   * mobile cells with useIsMobile). Return null for a row with none.
   */
  mobileRowActions?: (row: T) => ReactNode;
  emptyMessage?: ReactNode;
  /** Extra class on the outer wrapper (desktop card or mobile list). */
  className?: string;
  /**
   * Initial sort column key. When set AND that column has
   * `sortable: true`, the table mounts with that column active.
   * Subsequent header clicks override.
   */
  initialSortKey?: string;
  /** Initial sort direction. Default 'asc'. */
  initialSortDir?: 'asc' | 'desc';
}

/**
 * ResponsiveTable — renders a semantic `<table>` on desktop and a
 * stacked card list on mobile (<640px). Column authors describe which
 * cells should become the card title / subtitle / body / hidden via
 * the `mobile` marker, so the mobile layout stays legible without
 * duplicating the column definitions.
 *
 * Uses `useIsMobile()` — a JS viewport check rather than a CSS-only
 * duplication — because the desktop DOM is a full `<table>` and the
 * mobile DOM is a series of `<button>` cards. They can't share nodes.
 */
export function ResponsiveTable<T>({
  columns, rows, rowKey, onRowClick, mobileRowActions, onSortChange, mobileSort = false, serverSorted = false,
  emptyMessage = 'No results',
  className,
  initialSortKey,
  initialSortDir = 'asc',
}: ResponsiveTableProps<T>) {
  const isMobile = useIsMobile();

  // Sort state lives at the container level so it survives the
  // mobile↔desktop layout swap on viewport change. Backward-compat:
  // when no column declares `sortable`, the state stays inert and
  // rows render in input order — existing consumers are unaffected.
  const hasSortable = columns.some((c) => c.sortable);
  const [sortKey, setSortKey] = useState<string | undefined>(initialSortKey);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);

  const sortedRows = useMemo(() => {
    if (serverSorted || !hasSortable || !sortKey) return rows;
    const activeCol = columns.find((c) => c.key === sortKey && c.sortable);
    if (!activeCol) return rows;
    const getVal = activeCol.sortKey
      ?? ((r: T) => {
        // Fallback: stringify the cell render. Loses numeric
        // ordering on numeric columns; consumers should pass an
        // explicit `sortKey` for those.
        const rendered = activeCol.cell(r);
        return typeof rendered === 'string' || typeof rendered === 'number'
          ? rendered
          : String(rendered ?? '');
      });
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av instanceof Date && bv instanceof Date) {
        return sortDir === 'asc'
          ? av.getTime() - bv.getTime()
          : bv.getTime() - av.getTime();
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? '');
      const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return sorted;
  }, [rows, sortKey, sortDir, columns, hasSortable, serverSorted]);

  const handleSort = (colKey: string) => {
    const col = columns.find((c) => c.key === colKey);
    const nextDir = sortKey === colKey ? sortDir === 'asc' ? 'desc' : 'asc'
      : col?.sortKey ? 'desc' : 'asc';
    setSortKey(colKey);
    setSortDir(nextDir);
    onSortChange?.(colKey, nextDir);
  };

  if (rows.length === 0) {
    return (
      <div className={`${styles.empty}${className ? ` ${className}` : ''}`}>
        {emptyMessage}
      </div>
    );
  }

  return isMobile
    ? <>
      {mobileSort && hasSortable && <div className={styles.mobileSort}>
        <label>Sort by <select aria-label="Sort table by" value={sortKey ?? ''} onChange={e => handleSort(e.target.value)}>
          <option value="" disabled>Choose column</option>
          {columns.filter(c => c.sortable).map(c => <option key={c.key} value={c.key}>{typeof c.header === 'string' ? c.header : c.key}</option>)}
        </select></label>
        {sortKey && <button type="button" onClick={() => handleSort(sortKey)}>{sortDir === 'asc' ? 'Ascending' : 'Descending'}</button>}
      </div>}
      <MobileCardList columns={columns} rows={sortedRows} rowKey={rowKey} onRowClick={onRowClick} mobileRowActions={mobileRowActions} className={className} />
    </>
    : (
      <DesktopTable
        columns={columns}
        rows={sortedRows}
        rowKey={rowKey}
        onRowClick={onRowClick}
        className={className}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
      />
    );
}

function DesktopTable<T>({
  columns, rows, rowKey, onRowClick, className,
  sortKey, sortDir, onSort,
}: Omit<ResponsiveTableProps<T>, 'emptyMessage' | 'initialSortKey' | 'initialSortDir'> & {
  sortKey?: string;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
}) {
  return (
    <div className={`${styles.desktopCard}${className ? ` ${className}` : ''}`}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => {
                const isActive = col.sortable && sortKey === col.key;
                const arrow = isActive ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
                return (
                  <th
                    key={col.key}
                    style={{ textAlign: col.align ?? 'left', width: col.width }}
                    aria-sort={
                      isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => onSort(col.key)}
                        className={styles.sortHeaderBtn}
                      >
                        {col.header}
                        <span className={styles.sortArrow}>{arrow}</span>
                      </button>
                    ) : col.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? styles.rowClickable : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{ textAlign: col.align ?? 'left' }}
                  >
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MobileCardList<T>({
  columns, rows, rowKey, onRowClick, mobileRowActions, className,
}: Omit<ResponsiveTableProps<T>, 'emptyMessage'>) {
  // Non-clickable records may contain action buttons. A disabled button
  // wrapper prevents those actions and produces invalid nested controls.
  const Card = onRowClick ? 'button' : 'article';
  // Column role resolution for mobile card shape:
  //   primary:   explicit `mobile: 'primary'`; else the first column
  //   secondary: explicit `mobile: 'secondary'`; optional
  //   body:      explicit `mobile: 'body'` OR any other column whose
  //              mobile role wasn't set and isn't the fallback primary
  //   hidden:    dropped
  const primary = columns.find((c) => c.mobile === 'primary') ?? columns[0];
  const secondary = columns.find((c) => c.mobile === 'secondary');
  const bodyCols = columns.filter((c) => {
    if (c === primary || c === secondary) return false;
    if (c.mobile === 'hidden') return false;
    // Unset mobile role falls through as body.
    return c.mobile === 'body' || !c.mobile;
  });

  return (
    <div className={`${styles.mobileList}${className ? ` ${className}` : ''}`}>
      {rows.map((row) => {
        const actions = mobileRowActions?.(row) ?? null;
        const card = (
          <Card
            key={actions ? undefined : rowKey(row)}
            type={onRowClick ? 'button' : undefined}
            className={styles.mobileCard}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            data-clickable={onRowClick ? 'true' : 'false'}
          >
            {primary && (
              <div className={styles.mobilePrimary}>{primary.cell(row)}</div>
            )}
            {secondary && (
              <div className={styles.mobileSecondary}>{secondary.cell(row)}</div>
            )}
            {bodyCols.length > 0 && (
              <dl className={styles.mobileBody}>
                {bodyCols.map((col) => (
                  <div key={col.key} className={styles.mobileBodyRow}>
                    <dt>{col.header}</dt>
                    <dd>{col.cell(row)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Card>
        );
        // The row's own buttons sit beside the card, never inside it.
        return actions ? (
          <div key={rowKey(row)} className={styles.mobileCardGroup}>
            {card}
            <div className={styles.mobileActions}>{actions}</div>
          </div>
        ) : card;
      })}
    </div>
  );
}
